import * as React from "react";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import UserNotification from "util/UserNotification";
import { bulkCheckWatchlists } from "watchlist/hooks/useWatchlistsV2";
import defaultWatchlistFieldTypes from "security-content/mappings/watchlistFields";
import useLicenseCheck from "license/useLicenseCheck";
import { parseIfArrayValue } from "search/messageFieldValueHelper";
import WatchlistStatusContext from "./WatchlistStatusContext";
const parseFieldValue = (messageFieldValue) => {
  const parsedArrayValue = parseIfArrayValue(messageFieldValue);
  return parsedArrayValue != null ? parsedArrayValue : messageFieldValue;
};
const getMatchingFields = (watchlistFields, messageFields) => watchlistFields.reduce((matches, fieldName) => {
  const messageFieldValue = messageFields[fieldName];
  if (messageFieldValue !== void 0) {
    return {
      ...matches,
      [fieldName]: parseFieldValue(messageFieldValue)
    };
  }
  return matches;
}, {});
const getMatchingWatchlistFieldsByType = (watchlistFieldTypes, messageFields) => Object.entries(watchlistFieldTypes).reduce((result, [watchlistFieldType, watchlistFields]) => {
  const matchingFields = getMatchingFields(watchlistFields, messageFields);
  if (Object.values(matchingFields).length !== 0) {
    return {
      ...result,
      [watchlistFieldType]: matchingFields
    };
  }
  return result;
}, {});
const toStringArray = (value) => (Array.isArray(value) ? value : [value]).filter((v) => v !== void 0 && v !== null).map(String);
const checkWatchlistStatus = (fields) => {
  const checks = Object.entries(fields).map(([watchlistFieldType, fieldValues]) => ({
    type: watchlistFieldType,
    field_values: Object.fromEntries(
      Object.entries(fieldValues != null ? fieldValues : {}).map(([fieldName, value]) => [fieldName, toStringArray(value)])
    )
  }));
  return bulkCheckWatchlists(checks).then(
    (response) => response.results.reduce(
      (status, {
        watchlist_id: watchlistId,
        watchlist_name: watchlistName,
        watchlist_type: type,
        matched_fields: matchedFields
      }) => {
        var _a;
        if (!type || !matchedFields) {
          return status;
        }
        const matches = Object.entries(matchedFields).flatMap(
          ([fieldName, fieldMatches]) => fieldMatches.map((match) => ({
            fieldName,
            watchlistId,
            watchlistName,
            matchedEntry: match.matched_entry,
            matchedViaRange: match.matched_via_range
          }))
        );
        if (matches.length === 0) {
          return status;
        }
        return { ...status, [type]: [...(_a = status[type]) != null ? _a : [], ...matches] };
      },
      {}
    )
  ).catch((error) => {
    UserNotification.error(`Checking watchlist status for message fields failed with status: ${String(error)}`);
    return void 0;
  });
};
const filterEmptyLists = (fieldsWatchlistStatus) => {
  if (!fieldsWatchlistStatus) {
    return void 0;
  }
  return Object.entries(fieldsWatchlistStatus).reduce((lists, [watchlistFieldType, watchlistFields]) => {
    if (!watchlistFields || watchlistFields.length === 0) {
      return lists;
    }
    return { ...lists, [watchlistFieldType]: watchlistFields };
  }, {});
};
const WatchlistStatusProvider = ({ children, message, watchlistFieldTypes = defaultWatchlistFieldTypes }) => {
  const matchingWatchlistFieldsByType = getMatchingWatchlistFieldsByType(watchlistFieldTypes, message.fields);
  const shouldCheckStatus = Object.keys(matchingWatchlistFieldsByType).length !== 0;
  const {
    security: { isValid: isValidSecurityLicense }
  } = useLicenseCheck();
  const {
    data: fieldsWatchlistStatus,
    isPending,
    refetch
  } = useQuery({
    // matchingWatchlistFieldsByType is derived deterministically from the message, which is identified by message.id
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["watchlist", "status", message.id],
    queryFn: () => checkWatchlistStatus(matchingWatchlistFieldsByType),
    enabled: !!(shouldCheckStatus && isValidSecurityLicense)
  });
  const contextValue = useMemo(
    () => ({
      refetch,
      isLoading: isPending,
      status: filterEmptyLists(fieldsWatchlistStatus)
    }),
    [fieldsWatchlistStatus, isPending, refetch]
  );
  return /* @__PURE__ */ React.createElement(WatchlistStatusContext.Provider, { value: contextValue }, children);
};
export default WatchlistStatusProvider;
